/**
 * Light/dark mode: the state, the toggle, and the `ThemeProvider` that applies it.
 *
 * Mounted once in `main.tsx`, above everything. The chosen mode is remembered in
 * `localStorage`; with nothing remembered it follows the operating system, and
 * keeps following it until the user actually picks one.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createAppTheme } from './theme';

const STORAGE_KEY = 'forsys.colorMode';

/** What the user chose. `system` means "whatever the OS says, including later". */
export type ColorModePreference = PaletteMode | 'system';

export interface ColorModeContextValue {
  /** The user's choice. */
  preference: ColorModePreference;
  /** What is actually rendered right now — `system` already resolved. */
  mode: PaletteMode;
  setPreference: (preference: ColorModePreference) => void;
  /** Flips light↔dark, pinning the result. The header button calls this. */
  toggle: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

function readStored(): ColorModePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing, or storage disabled. Following the OS is a fine default.
  }
  return 'system';
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ColorModePreference>(readStored);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const mode: PaletteMode =
    preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;

  const setPreference = useCallback((next: ColorModePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice must not stop it taking effect.
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setPreference]);

  // Rebuilt only when the mode actually changes — createTheme is not cheap, and
  // a new theme object re-renders every styled component beneath it.
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Tells the browser to paint form controls, scrollbars and the canvas behind
  // the app to match. Without it a dark app keeps a white scrollbar.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ preference, mode, setPreference, toggle }),
    [preference, mode, setPreference, toggle],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {/* Normalises browser defaults and applies the theme background. Mount once. */}
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used inside <ColorModeProvider>.');
  }
  return context;
}
